import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Link } from '@backstage/ui';
import { RegionChip } from '../RegionChip';
import {
  getRuleSeverityFromResources,
  scanLabel,
  SEVERITY_COLORS,
  severityForeground,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ModernRightDrawer,
  useCloudResourcesAffectedByRule,
} from '@internal/backstage-plugin-devops-react';
import { useRulesContext } from '../Providers';
import s from './short-info.module.css';

/** Drawer pagination for affected resources (compliance proposal: a window
 * instead of the full unpaginated table). */
const RESOURCES_WINDOW = 5;
const RESOURCES_STEP = 10;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className={s.secTitle}>{title}</div>
      {children}
    </>
  );
}

/** Severity pill matching the mockup's colored badges. */
function SeverityPill({ severity }: { severity: string }) {
  const known = SEVERITY_COLORS[severity];
  return (
    <span
      className={s.pill}
      style={
        known
          ? {
              backgroundColor: known,
              color: severityForeground(severity),
            }
          : undefined
      }
    >
      {severity}
    </span>
  );
}

export function ShortInfoRuleDrawer() {
  const { selectedRule, unselectRule } = useRulesContext();
  const { affectedCloudResources } =
    useCloudResourcesAffectedByRule(selectedRule);
  const [visibleCount, setVisibleCount] = useState(RESOURCES_WINDOW);

  // Reset the window when another rule opens.
  useEffect(() => {
    setVisibleCount(RESOURCES_WINDOW);
  }, [selectedRule]);

  const closeShortInfo = () => unselectRule();

  // Worst severity across the rule's affected resources.
  const severity =
    getRuleSeverityFromResources(
      selectedRule?.spec?.resources as Array<{ severity?: string }>,
    ) ?? 'Unknown';

  // Per-resource severity (a rule can be High on one resource, Low on
  // another).
  const severityByResourceId = new Map(
    ((selectedRule?.spec?.resources ?? []) as Array<{
      id: string;
      severity?: string;
    }>).map(resource => [resource.id, resource.severity]),
  );

  // Framework memberships of the rule (RuleSet relations; the account
  // relation is partOf too, so scope to ruleset refs). The bare name drops
  // the `<accountId>_` prefix for readability.
  const frameworks = [...new Set((selectedRule?.relations ?? [])
    .filter(relation => relation.type === 'partOf')
    .filter(relation => relation.targetRef.startsWith('ruleset:'))
    .map(relation => relation.targetRef.split('/').pop() ?? '')
    .filter(Boolean))];

  const hiddenCount = Math.max(0, affectedCloudResources.length - visibleCount);

  const header = selectedRule ? (
    <div className={s.head}>
      <div className={s.headMain}>
        <span className={s.kicker}>Rule details</span>
        <h2 className={s.headTitle}>
          {selectedRule.metadata.title ?? selectedRule.metadata.name}
        </h2>
        <div className={s.badges}>
          <SeverityPill severity={severity} />
          <span className={s.badge}>{selectedRule.spec?.result ?? 'Unknown'}</span>
          {selectedRule.spec?.category && (
            <span className={s.badge}>{selectedRule.spec.category}</span>
          )}
        </div>
      </div>
    </div>
  ) : undefined;

  const footer = selectedRule ? (
    <div className={s.foot}>
      <Link
        className={s.footLink}
        href={`/catalog/default/rule/${selectedRule.metadata.name}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open full rule page ↗
      </Link>
      <span
        className={s.footNote}
        title={selectedRule.spec?.reportCreationTime ?? undefined}
      >
        {selectedRule.spec?.reportCreationTime
          ? `Last scan ${scanLabel(selectedRule.spec.reportCreationTime)}`
          : ''}
      </span>
    </div>
  ) : undefined;

  return (
    <ModernRightDrawer
      title={selectedRule?.spec.type ?? ''}
      header={header}
      footer={footer}
      isOpen={selectedRule !== undefined}
      onClose={closeShortInfo}
      width={520}
    >
      {selectedRule && (
        <>
          {selectedRule.metadata.description && (
            <p className={s.desc}>{selectedRule.metadata.description}</p>
          )}

          <div className={s.secTitle}>Overview</div>
          <div className={s.metaGrid}>
            <div>
              <div className={s.k}>Rule code</div>
              <div className={s.v}>{selectedRule.spec.type}</div>
            </div>
            {selectedRule.spec.resourceType && (
              <div>
                <div className={s.k}>Resource type</div>
                <div className={s.v}>{selectedRule.spec.resourceType}</div>
              </div>
            )}
            {selectedRule.spec.category && (
              <div>
                <div className={s.k}>Category</div>
                <div className={s.v}>{selectedRule.spec.category}</div>
              </div>
            )}
            <div>
              <div className={s.k}>Affected resources</div>
              <div className={s.v}>{affectedCloudResources.length}</div>
            </div>
          </div>

          {selectedRule.spec.result === 'Unknown' &&
            (selectedRule.spec.unknownLocations?.length ?? 0) > 0 && (
              <Section title="Unknown locations">
                <div className={s.chips}>
                  {(selectedRule.spec.unknownLocations ?? []).map(location => (
                    <span key={location.name} className={s.badge}>
                      {location.name}
                      {location.note ? ` — ${location.note}` : ''}
                    </span>
                  ))}
                </div>
              </Section>
            )}

          {frameworks.length > 0 && (
            <Section title="Included in">
              <div className={s.chips}>
                {frameworks.map(rulesetName => (
                  <Link
                    key={rulesetName}
                    className={s.badge}
                    href={`/catalog/default/ruleset/${rulesetName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {rulesetName}
                  </Link>
                ))}
              </div>
            </Section>
          )}

          <Section title="Affected resources">
            <table className={s.resTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Region</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {affectedCloudResources
                  .slice(0, visibleCount)
                  .map(resource => {
                    const resourceSeverity =
                      severityByResourceId.get(resource.metadata.name) ??
                      severity;
                    const region = resource.spec?.region;
                    return (
                      <Fragment key={resource.metadata.name}>
                        <tr>
                          <td>{resource.metadata.title ?? resource.metadata.name}</td>
                          <td>
                            <RegionChip
                              region={typeof region === 'string' ? region : ''}
                            />
                          </td>
                          <td>
                            <SeverityPill severity={resourceSeverity} />
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
              </tbody>
            </table>
            {hiddenCount > 0 && (
              <button
                type="button"
                className={s.showMore}
                onClick={() => setVisibleCount(count => count + RESOURCES_STEP)}
              >
                Show 10 more ({hiddenCount} hidden)
              </button>
            )}
          </Section>
        </>
      )}
    </ModernRightDrawer>
  );
}
